import { DEFAULT_LANGUAGE } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { type AvailableDates } from '@avanta/shared/types/appointments';
import { getAvailableDatesFromDailySlots } from '@avanta/shared/utils/appointments';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentsReservePayload } from '../../../../types';

interface UseFetchFreeHoursReturn {
  availableDates: AvailableDates;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export const useFetchFreeHours = (): UseFetchFreeHoursReturn => {
  const { watch } = useFormContext();
  const { state } = useLocation();
  const patientId = watch(AppointmentsReservePayload.patientId) || state?.patientId;

  const [backofficeCenterId, specialityId] = watch([
    AppointmentsReservePayload.backofficeCenterId,
    AppointmentsReservePayload.specialityBooked,
  ]);

  const isCalendarQueryEnabled = !!specialityId && !!backofficeCenterId && !!patientId;

  const {
    data: freeHoursData,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_FREE_HOURS_AVAILABILITY,
      patientId,
      specialityId,
      backofficeCenterId,
    ],
    queryFn: () =>
      AppointmentService.getFreeHoursAvailability({
        patientId,
        specialityId,
        centerId: backofficeCenterId,
        spokenLanguage: DEFAULT_LANGUAGE,
      }),
    select: response => response.data,
    enabled: isCalendarQueryEnabled,
    placeholderData: keepPreviousData,
  });

  const availableDates = useMemo(
    () => (freeHoursData ? getAvailableDatesFromDailySlots(freeHoursData.dailySlots) : {}),
    [freeHoursData]
  );

  return { availableDates, isLoading, isFetching, isError };
};
